import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Shield, Eye, EyeOff, ArrowRight, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useRegion } from '../../hooks/useRegion';
import { REGIONS as REGION_MAP } from '../../config/regions';
import { LanguageSelector } from '../../components/LanguageSelector';
import InfoTooltip from '../../components/InfoTooltip';

const COUNTRIES = [
  { code: 'IN', name: '🇮🇳 India' },
  { code: 'SG', name: '🇸🇬 Singapore' },
  { code: 'MY', name: '🇲🇾 Malaysia' },
  { code: 'PH', name: '🇵🇭 Philippines' },
  { code: 'ID', name: '🇮🇩 Indonesia' },
] as const;

const PHONE_PLACEHOLDER: Record<string, string> = {
  IN: '+91 98765 43210',
  SG: '+65 9123 4567',
  MY: '+60 12 345 6789',
  PH: '+63 917 123 4567',
  ID: '+62 812 3456 7890',
};

type CountryCode = typeof COUNTRIES[number]['code'];

export default function Register() {
  const nav = useNavigate();
  const region = useRegion();
  const [params] = useSearchParams();
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    country: params.get('country') || region.region,
    plan_id: params.get('plan') || 'starter',
    referral_code: params.get('ref') || undefined,
  });

  const up = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const { i18n } = useTranslation();

  // Detect language/country mismatch so we can warn the user
  const selectedRegionConfig = REGION_MAP[form.country as CountryCode];
  const activeLang = localStorage.getItem('vantag_lang') || i18n.language;
  const langValidForCountry = selectedRegionConfig?.languages.some((l) => l.code === activeLang) ?? true;
  const langMismatch = !langValidForCountry;
  /** Language we'll actually send to the backend: user's choice if valid, else country default */
  const resolvedLanguage = langValidForCountry
    ? activeLang
    : (selectedRegionConfig?.defaultLang ?? 'en');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post('/api/auth/register', { ...form, language: resolvedLanguage });
      localStorage.setItem('vantag_token', data.access_token);
      localStorage.setItem('vantag_refresh_token', data.refresh_token);
      localStorage.setItem('vantag_tenant', JSON.stringify({ id: data.tenant_id, plan: data.plan_id, step: 1 }));
      toast.success('Account created! Please verify your email.');
      nav('/verify-email', { state: { email: form.email } });
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md">
        <div className="flex justify-center mb-8">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold">{region.brandShort}</span>
          </Link>
        </div>
        <div className="absolute top-0 right-0">
          <LanguageSelector variant="light" />
        </div>
        <div className="bg-white/3 border border-white/8 rounded-2xl p-8">
          <h1 className="text-2xl font-bold mb-2 text-center">Create your account</h1>
          <p className="text-white/40 text-sm text-center mb-8">3-day free trial · No credit card required</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm text-white/60 flex items-center mb-1.5">
                Shop Name
                <InfoTooltip text="Enter your store or business name. This appears on your dashboard and reports." />
              </label>
              <input required value={form.name} onChange={e => up('name', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-colors"
                placeholder="My Retail Shop" />
            </div>
            <div>
              <label className="text-sm text-white/60 flex items-center mb-1.5">
                Email
                <InfoTooltip text="Your work email address. We'll send alerts and important notifications here." />
              </label>
              <input type="email" required value={form.email} onChange={e => up('email', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-colors"
                placeholder="owner@shop.com" />
            </div>
            <div>
              <label className="text-sm text-white/60 flex items-center mb-1.5">
                Phone Number
                <InfoTooltip text="Your mobile number for SMS alerts. Include country code (e.g. +63 for Philippines, +91 for India)." />
              </label>
              <input type="tel" value={form.phone} onChange={e => up('phone', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-colors"
                placeholder={PHONE_PLACEHOLDER[form.country] ?? '+91 98765 43210'} />
            </div>
            <div>
              <label className="text-sm text-white/60 flex items-center mb-1.5">
                Country
                <InfoTooltip text="Select the country where your store operates. This sets your currency and compliance defaults." />
              </label>
              <select value={form.country} onChange={e => up('country', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-violet-500/50 transition-colors">
                {COUNTRIES.map(c => <option key={c.code} value={c.code} className="bg-gray-900">{c.name}</option>)}
              </select>
              {langMismatch && (
                <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-400 text-xs">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    Your current UI language (<strong>{activeLang}</strong>) is not available for {selectedRegionConfig?.country ?? form.country}.
                    Your account will be set to <strong>{resolvedLanguage}</strong> — you can change it after login.
                  </span>
                </div>
              )}
            </div>
            <div>
              <label className="text-sm text-white/60 flex items-center mb-1.5">
                Password
                <InfoTooltip text="Choose a strong password with at least 8 characters. You'll use this to log in to your Vantag dashboard." />
              </label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} required value={form.password} onChange={e => up('password', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-12 text-white placeholder-white/20 focus:outline-none focus:border-violet-500/50 transition-colors"
                  placeholder="Min 8 characters" />
                <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading}
              className="w-full py-3.5 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all mt-2">
              {loading ? 'Creating account...' : <><span>Create Account & Start Free Trial</span><ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-center text-white/30 text-xs">
              Your 3-day free trial starts automatically — no card, no charges. You'll confirm your plan in the next step.
            </p>
          </form>
          <p className="text-center text-white/40 text-sm mt-6">
            Already have an account? <Link to="/login" className="text-violet-400 hover:text-violet-300">Sign in</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
